import type { FormVersion } from '../types';
import { newId } from './id';

/**
 * Pure version-lifecycle helpers — the explicit, non-destructive replacement
 * for the legacy ancestor_id copy-on-write row pattern. Shared by the demo
 * host's mock persistence layer and (eventually) any real backend, so the
 * state machine only needs to be correct in one place.
 */

export function nextVersionNumber(versions: FormVersion[], formId: string): number {
  const forForm = versions.filter((v) => v.formId === formId);
  return forForm.length === 0 ? 1 : Math.max(...forForm.map((v) => v.versionNumber)) + 1;
}

export function createDraftFromVersion(
  source: FormVersion,
  versions: FormVersion[],
  who?: string,
): FormVersion {
  const now = new Date().toISOString();
  return {
    formVersionId: newId(),
    formId: source.formId,
    versionNumber: nextVersionNumber(versions, source.formId),
    status: 'draft',
    document: structuredClone(source.document),
    previousVersionId: source.formVersionId,
    createdAt: now,
    createdBy: who,
    updatedAt: now,
    updatedBy: who,
  };
}

/** Promotes a draft to published; any other published version for the same form should be archived by the caller. */
export function publishVersion(draft: FormVersion, who?: string): FormVersion {
  return { ...draft, status: 'published', updatedAt: new Date().toISOString(), updatedBy: who };
}

export function archiveVersion(version: FormVersion, who?: string): FormVersion {
  return { ...version, status: 'archived', updatedAt: new Date().toISOString(), updatedBy: who };
}
